import { useMutation } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useAppStore } from '../../app/appStore';
import { landingLanguage, offeredLanguages } from '../../shared/country/countryConfig';
import { useCountryStore } from '../../shared/country/countryStore';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
} from '../../shared/i18n/messages';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { checkUsername, updateMeUsername } from './api';
import { useAuthStore } from './authStore';
import { ForgotForm } from './ForgotForm';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { BrandMini, errorPayload, readAuthPrefs, scrollAuthInputIntoView, validateUsername, AuthLegalFoot } from './authShared';
import { isGeneratedUsername, poolForAccount } from '../../shared/username/generator';
import { suggestAvailableUsername } from '../../shared/username/suggest';
import { USERNAME_MAX_LEN } from '../../shared/username/validateUsername';

type AuthMode = 'login' | 'register' | 'forgot' | 'username';

export function AuthPage({ onAuthenticated }: { onAuthenticated: () => void }) {
  const route = useAppStore((state) => state.route);
  // Derived directly from `route`, not mirrored into its own state-plus-effect.
  // That indirection used to cost a whole extra render: `route` would change,
  // this component would re-render still holding the *old* mode for one pass,
  // and only a subsequent effect would call `setMode`. Anything that needed
  // to react to "mode just became X" — see `registerInitialIdentifier` below —
  // was therefore always one render behind the children that mount on that
  // same mode change, which is exactly what let a stale value slip into
  // `RegisterForm`'s `useState` before the correction ever arrived. Reading
  // `mode` straight from `route` keeps it synchronous with everything that
  // renders off of it.
  const mode = authModeFromRoute(route);
  const [resetSuccess, setResetSuccess] = useState(false);
  // The identifier a player is mid-way through typing, carried across a mode
  // switch (Login ⇄ Register ⇄ Forgot) the same way it did back when all
  // three modes were one component sharing a single `identifier` state.
  // Splitting into separate components means each form's own `identifier`
  // state starts fresh on mount — without this, "no account, want to
  // register?" and "forgot password?" would both drop what was just typed,
  // making the player retype a phone number the app already has on screen.
  const [identifierDraft, setIdentifierDraft] = useState(() => readAuthPrefs().identifier);
  const onIdentifierDraftChange = useCallback((value: string) => setIdentifierDraft(value), []);
  // Same idea for "remember me": Login is the only screen with the checkbox,
  // but Register's `writeAuthPrefs`/`setSession` calls need whatever the
  // player last chose there — unchecking it and then registering must not
  // silently go back to a persistent (localStorage) session.
  const [remember, setRemember] = useState(true);
  const [username, setUsername] = useState('');
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const logout = useAuthStore((state) => state.logout);
  const showToast = useAppStore((state) => state.showToast);
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const setLanguage = useI18nStore((state) => state.setLanguage);
  const country = useCountryStore((state) => state.country);
  const loadCountry = useCountryStore((state) => state.load);
  const loadCountrySuggestion = useCountryStore((state) => state.loadSuggestion);

  // The remembered identifier is a *login* affordance. Carried into "Create account"
  // it puts the previous account's handle in front of a brand-new user and fails
  // validation on arrival ("Use email or phone to create your account"), so the first
  // screen of the signup flow opens in an error state. Only the untouched prefill is
  // cleared — anything typed before switching modes is the user's own and stays.
  //
  // Computed at render time, not in a `useEffect(() => ..., [mode])`: an effect
  // only runs *after* the render that first shows `mode === 'register'` has
  // already committed — and `RegisterForm` mounts (and locks its `identifier`
  // state via `useState(initialIdentifier)`) in that same first render. By the
  // time an effect could clear `identifierDraft`, the stale value is already
  // baked into the child; a later prop change does not reach a `useState`
  // initializer. Deriving the value inline instead means the very render that
  // first mounts `RegisterForm` already passes the corrected one.
  const registerInitialIdentifier = mode === 'register'
    ? (() => {
        const remembered = readAuthPrefs().identifier;
        return remembered && identifierDraft === remembered ? '' : identifierDraft;
      })()
    : identifierDraft;

  // Belt-and-braces: `App` loads the list for every session. Kept here because the
  // phone prefix and the registration notice are unusable without it, and the store
  // dedupes concurrent calls for both `load` and `loadSuggestion` — this effect
  // firing alongside `App`'s (or twice under React.StrictMode) does not double the
  // network traffic.
  //
  // `loadSuggestion` goes alongside `load`, concurrently rather than chained: the
  // two are independent, so whichever of `countries` or `suggestedCode` arrives
  // first does not block the other.
  useEffect(() => {
    void loadCountry();
    void loadCountrySuggestion();
  }, [loadCountry, loadCountrySuggestion]);

  // Leave a language the new country does not offer.
  //
  // `available_locales` is the operator's statement of what a market shows, so
  // sitting in an unlisted language contradicts it — and the pill bar then has
  // no active entry, which reads as a broken control with no way back. Falls to
  // the country's default locale (D-01: Argentina lists en before es, and taking
  // the first entry put a Portuguese phone into English rather than Spanish).
  //
  // Only fires when the current language is genuinely unavailable, so it never
  // overrides a choice the country actually supports. Lives in the shell (not a
  // per-form hook) because it has to run regardless of which mode — including
  // username setup — is on screen. Guarded on the country code actually
  // changing (not just the store object's identity) — the original combined
  // effect this was split from (Task 9) only ran on a genuine country change,
  // and losing that guard would let a saved language choice get silently
  // overwritten on every unrelated store update, not just when the active
  // country's `available_locales` narrows.
  const lastLanguageCheckCountryCodeRef = useRef(country.code);
  useEffect(() => {
    if (lastLanguageCheckCountryCodeRef.current === country.code) return;
    lastLanguageCheckCountryCodeRef.current = country.code;
    const offered = offeredLanguages(country.availableLocales, LANGUAGES, DEFAULT_LANGUAGE);
    if (!offered.includes(language)) {
      setLanguage(landingLanguage(country.availableLocales, country.defaultLocale, LANGUAGES, DEFAULT_LANGUAGE));
    }
  }, [country, language, setLanguage]);

  // Seed the field ONCE per entry into username mode — with the user's existing
  // handle, or a suggestion. Must not depend on `username`: otherwise clearing the
  // field (to type your own) re-fires this and pops a fresh suggestion back in,
  // which is exactly the "I delete it and another one appears" bug. Rerolling is
  // the 🎲 button's job, not an effect's.
  // Every suggestion request gets a sequence number; only the latest one may
  // write into the field, so a slow prefill cannot overwrite a later 🎲 roll.
  const suggestSeq = useRef(0);

  const requestSuggestion = useCallback(async () => {
    const seq = ++suggestSeq.current;
    const pool = poolForAccount(user, language);
    const name = await suggestAvailableUsername(pool, (candidate) => checkUsername(token, candidate).then((r) => r.available));
    if (seq === suggestSeq.current) setUsername(name);
  }, [language, token, user]);

  const usernameSeeded = useRef(false);
  useEffect(() => {
    if (mode !== 'username') {
      usernameSeeded.current = false;
      return;
    }
    if (usernameSeeded.current) return;
    usernameSeeded.current = true;
    const current = normalizeUsernameInput(user?.username || user?.name || '');
    if (current && !isGeneratedUsername(current, user?.country_code)) {
      setUsername(current);
      return;
    }
    void requestSuggestion();
  }, [mode, requestSuggestion, user?.country_code, user?.name, user?.username]);

  // Legacy auto-dismisses the reset-success banner after 30s.
  useEffect(() => {
    if (!resetSuccess) return undefined;
    const timer = window.setTimeout(() => setResetSuccess(false), 30_000);
    return () => window.clearTimeout(timer);
  }, [resetSuccess]);

  const suggestTimestamps = useRef<number[]>([]);
  const accentToastShown = useRef(false);

  const usernameValidation = validateUsername(username);
  const usernameMutation = useMutation({
    mutationFn: () => {
      if (!token || !usernameValidation.ok) throw new Error('username_invalid');
      return updateMeUsername(token, { username: usernameValidation.lower, setup: true });
    },
    onSuccess: (payload) => {
      const savedUsername = payload.username || (usernameValidation.ok ? usernameValidation.lower : username);
      const nextUser = {
        ...(user || { id: 'me' }),
        ...(payload.user || {}),
        username: savedUsername,
        name: savedUsername,
        requires_username_setup: false,
        requiresUsernameSetup: false,
        username_changed_at: payload.next_change_at ?? payload.user?.username_changed_at ?? user?.username_changed_at,
        usernameChangedAt: payload.nextChangeAt ?? payload.user?.usernameChangedAt ?? user?.usernameChangedAt,
        username_grace_used: payload.used_grace ?? payload.user?.username_grace_used ?? user?.username_grace_used,
        usernameGraceUsed: payload.usedGrace ?? payload.user?.usernameGraceUsed ?? user?.usernameGraceUsed,
      };
      setUser(nextUser);
      showToast(t('usernameSaved'), 'success');
      onAuthenticated();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (usernameValidation.ok && !usernameMutation.isPending) usernameMutation.mutate();
  }

  function suggestUsername() {
    if (!canSuggestUsername(suggestTimestamps.current)) {
      showToast(t('usernameSuggestRateLimit'), 'warn');
      return;
    }
    usernameMutation.reset();
    void requestSuggestion();
  }

  function handleUsernameInput(raw: string) {
    // Discard any in-flight suggestion (prefill or 🎲) once the player types —
    // otherwise a slow round trip can land after they've started editing and
    // clobber what they typed.
    suggestSeq.current += 1;
    const normalized = normalizeUsernameInput(raw);
    if (normalized !== raw && !accentToastShown.current) {
      accentToastShown.current = true;
      showToast(t('usernameAccentRemoved'), 'info');
    }
    setUsername(normalized);
    usernameMutation.reset();
  }

  if (mode === 'username') {
    const validationMessage = username ? usernameValidation.ok ? '' : usernameValidationMessage(usernameValidation.code, t) : '';
    const anonWarning = usernameAnonWarning(username, t);
    return (
      <section className="auth-screen" data-auth-username-react="true">
        <div className="auth-orb orb-gold" aria-hidden="true" />
        <div className="auth-orb orb-brand" aria-hidden="true" />
        <div className="auth-orb orb-emerald" aria-hidden="true" />
        <div className="auth-grid" aria-hidden="true" />

        <div className="px-4 pt-4 pb-1 flex items-center justify-end relative z-[1]">
          <BrandMini />
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-4 relative z-[1]">
          <div className="text-center mb-5">
            <h1 className="text-ink-1 text-xl font-bold">{t('usernameTitle')}</h1>
            <p className="text-ink-3 text-xs mt-1.5">{t('usernameSubtitle')}</p>
          </div>

          <form className="auth-card" onSubmit={submit} onFocusCapture={scrollAuthInputIntoView}>
            <p className="text-[11px] text-emerald-300/85 mb-2 leading-snug">{t('usernameGuidance')}</p>
            <div className="auth-input-wrap">
              <input
                className={`auth-input pr-11${validationMessage ? ' auth-input-error' : ''}`}
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={USERNAME_MAX_LEN}
                value={username}
                onChange={(event) => handleUsernameInput(event.target.value)}
                placeholder={t('usernamePlaceholder')}
              />
              <button type="button" className="auth-input-eye text-base" onClick={suggestUsername} aria-label={t('usernameSuggestBtn')} title="🎲">🎲</button>
            </div>

            <p className={`text-[11px] mt-1 ml-1 min-h-[14px] ${validationMessage || usernameMutation.isError ? 'text-rose-400' : 'text-white/40'}`}>
              {usernameMutation.isError ? usernameServerError(usernameMutation.error, t) : validationMessage || ' '}
            </p>
            {anonWarning ? (
              <p className="text-[11px] text-amber-400/90 mt-1 ml-1 inline-flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{anonWarning}</span>
              </p>
            ) : null}

            <ul className="text-[11px] text-white/55 mt-3 space-y-1">
              {[t('usernameRuleLength'), t('usernameRuleFirst'), t('usernameRuleChars'), t('usernameRuleNoTrail')].map((rule) => (
                <li key={rule} className="flex items-center gap-1.5"><span className="text-emerald-400">·</span><span>{rule}</span></li>
              ))}
            </ul>

            <button className="btn-gold-primary w-full py-3 rounded-xl mt-4 transition-all tap" type="submit" disabled={!usernameValidation.ok || usernameMutation.isPending}>
              {t('usernameSubmit')}
            </button>
          </form>

          <div className="text-center mt-3">
            <button type="button" className="text-[12px] text-white/50 underline py-2 px-3" onClick={logout}>{t('signOut')}</button>
          </div>
          <AuthLegalFoot />
        </div>
      </section>
    );
  }

  return (
    <section className="auth-screen">
      <div className="auth-orb orb-gold" aria-hidden="true" />
      <div className="auth-orb orb-brand" aria-hidden="true" />
      <div className="auth-orb orb-emerald" aria-hidden="true" />
      <div className="auth-grid" aria-hidden="true" />

      {mode === 'login' ? (
        <LoginForm
          onAuthenticated={onAuthenticated}
          resetSuccess={resetSuccess}
          onDismissResetSuccess={() => setResetSuccess(false)}
          initialIdentifier={identifierDraft}
          onIdentifierChange={onIdentifierDraftChange}
          remember={remember}
          onRememberChange={setRemember}
        />
      ) : mode === 'register' ? (
        <RegisterForm
          onAuthenticated={onAuthenticated}
          initialIdentifier={registerInitialIdentifier}
          onIdentifierChange={onIdentifierDraftChange}
          remember={remember}
        />
      ) : (
        <ForgotForm
          onResetSuccess={() => setResetSuccess(true)}
          initialIdentifier={identifierDraft}
          onIdentifierChange={onIdentifierDraftChange}
        />
      )}
    </section>
  );
}

function authModeFromRoute(route: string): AuthMode {
  if (route === 'auth.register') return 'register';
  if (route === 'auth.forgot') return 'forgot';
  if (route === 'auth.username') return 'username';
  return 'login';
}

function usernameValidationMessage(code: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const map: Record<string, Parameters<typeof t>[0]> = {
    too_short: 'usernameErrTooShort',
    too_long: 'usernameErrTooLong',
    first_char: 'usernameErrFirstChar',
    invalid_char: 'usernameErrInvalidChar',
    consecutive_special: 'usernameErrConsecutive',
    trailing_special: 'usernameErrTrailing',
    reserved: 'usernameErrReserved',
    taken: 'usernameErrTaken',
  };
  return t(map[code] || 'usernameErrInvalid');
}

function usernameServerError(error: unknown, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const payload = errorPayload(error);
  const code = payload && typeof payload === 'object'
    ? String((payload as { reason?: string; code?: string }).reason || (payload as { code?: string }).code || '')
    : error instanceof Error ? error.message : 'invalid';
  if (code === 'cooldown_active') {
    const nextAt = payload && typeof payload === 'object' ? Number((payload as { next_change_at?: number; nextChangeAt?: number }).next_change_at ?? (payload as { nextChangeAt?: number }).nextChangeAt ?? 0) : 0;
    return t('usernameErrCooldown').replace('%date%', nextAt ? formatAuthDate(nextAt) : '');
  }
  if (code === 'same') return t('usernameErrSame');
  return usernameValidationMessage(code, t);
}

function usernameAnonWarning(raw: string, t: ReturnType<typeof useI18nStore.getState>['t']) {
  const valid = validateUsername(raw);
  if (!valid.ok) return '';
  const lower = valid.lower;
  const candidate = lower.replace(/[_.]+$/, '');
  const anonPrefixes = ['anon', 'anonymous', 'player', 'user', 'teste', 'test', 'demo', 'guest', 'visitante', 'bot', 'asdf', 'qwer'];
  for (const prefix of anonPrefixes) {
    if (candidate === prefix || candidate.startsWith(`${prefix}_`) || candidate.startsWith(`${prefix}.`) || (/^[a-z]+\d+$/.test(candidate) && candidate.startsWith(prefix))) return t('usernameAnonWarn');
  }
  if (!/[a-z]/.test(lower)) return t('usernameAnonWarn');
  return '';
}

function normalizeUsernameInput(raw: string) {
  return raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s/g, '').slice(0, USERNAME_MAX_LEN);
}

function canSuggestUsername(timestamps: number[]) {
  const now = Date.now();
  const recent = timestamps.filter((time) => now - time < 60_000);
  timestamps.splice(0, timestamps.length, ...recent);
  if (timestamps.length >= 5) return false;
  timestamps.push(now);
  return true;
}

function formatAuthDate(secondsOrMs: number) {
  const ms = secondsOrMs > 10_000_000_000 ? secondsOrMs : secondsOrMs * 1000;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(ms));
}

function showDemoFooter() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('demo') === '1';
}
